// Imports
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import selfActions from '../../../../../../redux/self/actions';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import sanitizeHTML from '../../../../../../shareables/front-end/utils/sanitize-html/sanitize-html';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetHomepageResponse } from '../../../../../../shareables/types/api/main/website/homepage/get';
import { GetVisibilityResponse } from '../../../../../../shareables/types/api/main/website/homepage/visibility/get';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import GoogleAds from '../../../google-ads';
import PageTitle from '../../shared-components/page-title';
import { StyledEditorGeneratedContentContainer } from '../shared-components/styled-editor-generated-content-container';
import useMemberCheck from '../../../../../../utils/use-member-check';


// Styled components
const Main = styled(StyledEditorGeneratedContentContainer)<{ theme: GeneratedThemeProps }>`
	line-height: 1.3125rem;
	overflow-wrap: anywhere;
	color: #333;
	padding-bottom: 0.6875rem;
	margin-bottom: 0.625rem;
	
	max-width: 100%;
	overflow: auto;
	vertical-align: top;
	
	grid-column: ${(props) => (props.theme.isFlipped ? 2 : 1)};
	
	p:last-child {
		margin-bottom: 0;
	}
`;


// Define the accepted props
interface Props {
	storybookHomepage?: GetHomepageResponse;
}


// Function component
const Homepage: React.FC<Props> = ({ storybookHomepage }) => {
	// Use state
	const [homepage, setHomepage] = useState<GetHomepageResponse | null>(storybookHomepage ?? null);
	
	
	// Initialize
	const communityDomain = window.location.hostname;
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const websitePlan = useAppSelector((state) => state.self.website?.plan);
	const isPageAdmin = useMemberCheck({ type: 'PRIVILEGE', privilege: 'PAGES' });
	
	
	// Load homepage and visibility on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetHomepageResponse>({
				action: 'GET',
				uri: `/website/${communityDomain}/homepage`,
				completion: (json) => {
					setHomepage(json.response);
				},
			})
		);
		
		void dispatch(
			apiActions.call<undefined, undefined, GetVisibilityResponse>({
				action: 'GET',
				uri: `/website/${communityDomain}/homepage/visibility`,
				completion: (json) => {
					dispatch(selfActions.storeHomepageVisibility(json.response));
				},
			})
		);
	}, [dispatch, communityDomain]);
	
	
	// If loading, then display nothing
	if (homepage === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle
				documentTitle={homepage.title}
				{...(isPageAdmin && { editPageDetails: { pageID: homepage.id, pageTypeID: 1 } })}
				appendCommunityName={false}
			>
				{homepage.header}
			</PageTitle>
			
			<Main
				dangerouslySetInnerHTML={{
					__html: sanitizeHTML(homepage.text ?? '<em>No text has been written for this page yet…</em>', {
						extendAllowedTags: ['iframe'],
					}),
				}}
			/>
			
			{websitePlan === 0 && homepage.text.length >= 200 && <GoogleAds />}
		</React.Fragment>
	);
};

export default Homepage;
